import { Typography } from '@material-ui/core'
import React from 'react'

import NoDataImage from '../../assets/images/No-Data.png'
import { getTranslate } from '../../localization'
import useStyles from './styles'

const t = getTranslate

/**
 * The portal's shared empty state.
 *
 * Lives in src/components/ rather than under one screen. It was written inside
 * Notifications first, which would have made it the THIRD private rendering of
 * one decision — BookingList and InventoryAllocateHistory each have their own.
 * That is the exact failure portal-ux-conventions.md §7 adopts the shared asset
 * to stop, so a screen-local copy would have re-enacted it while citing it.
 *
 * Replaces a bare `<Typography>` that read "No {label} found." — correct, but
 * invented here. `portal-ux-conventions.md` §7 adopts the centred `No-Data.png`
 * going forward, and is explicit that it is a decision rather than an observed
 * convention: only two screens use it today. It is the standard precisely
 * because the alternative — every screen inventing its own — is how the
 * divergence the document exists to stop accumulated.
 *
 * `role="status"` is carried over from the original. The image is decorative,
 * so its alt is empty and the label carries the meaning for a screen reader.
 */
export const EmptyState = ({
  label,
  message
}: {
  label: string
  /**
   * Overrides the default "No {label} found." — which asserts that none exist.
   * A caller filtering client-side needs to say something weaker, because it
   * only knows that none of the rows it has LOADED match. Already translated
   * by the caller, since only the caller knows what to interpolate.
   */
  message?: string
}) => {
  const classes = useStyles()

  return (
    <div className={classes.container} role="status">
      <img alt="" className={classes.image} src={NoDataImage} />
      <Typography
        className={classes.label}
        color="textSecondary"
        variant="body2">
        {message ?? t('No {label} found.', { label: t(label).toLowerCase() })}
      </Typography>
    </div>
  )
}
